import { useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { useTranslation } from 'react-i18next';
import { Theme } from '@app/theme';
import {
  Feature,
  Product,
  ProductType,
  useCountryByPriceGroups,
  useCountryPriceTiers,
} from '@app/api';
import { toEuros } from 'lib/numbers';
import { QuestionTooltip } from '@app/core';
import { getGroupProducts } from 'lib/planAndOffer';
import { compareProducts } from 'lib/products';
import { MinMaxTotal, ProductListSection } from 'components';
import classNames from 'classnames';
import {
  computeIncentive,
  NO_PRICE_GROUP,
  sumProducts,
  toProductArr,
} from 'lib/priceComputation';
import { SubTotal } from './SubTotal';
import { PriceTotals, ProductWithCountryGroup } from 'lib/priceComputation';

const useStyles = createUseStyles((theme: Theme) => ({
  label: {
    flex: 1,
  },
  total: {
    marginTop: theme.marginBase,
    marginBottom: theme.marginBase * 2,
    display: 'flex',
    alignItems: 'center',
    ...theme.fonts.h4,
  },
  price: {
    textAlign: 'right',
  },
  subPrice: {
    flex: 1,
    textAlign: 'right',
  },
  hr: {
    marginTop: theme.marginBase,
    marginBottom: theme.marginBase,
    width: '100%',
    borderBottom: {
      style: 'solid',
      width: 1,
      color: theme.colors.grayBorder,
    },
  },
  tooltip: {
    marginLeft: theme.marginBase,
  },
  subTotalBlock: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.std,
    padding: theme.marginBase * 2,
  },
  subTotalLine: {
    ...theme.fonts.h5,
    display: 'flex',
  },
  taxesLine: {
    marginTop: theme.marginBase / 2,
    ...theme.fonts.caption,
    display: 'flex',
  },
  container: {
    paddingTop: theme.marginBase * 3,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
  },
}));

interface Props {
  products: ProductWithCountryGroup[];
  countryKeys?: string[];
  duration?: number;
  numberOfParticipants?: number;
  className?: string;
  totalOnly?: boolean;
  tooltip?: string;
  defaultOpened?: boolean;
  totals: PriceTotals;
}

export const PriceDetails = ({
  products,
  countryKeys,
  duration = 60,
  numberOfParticipants = 1,
  totalOnly,
  className,
  tooltip,
  defaultOpened,
  totals,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const countryByPriceGroups = useCountryByPriceGroups(countryKeys);
  const countryPriceGroupIds = useCountryPriceTiers(countryKeys);

  const relevantProducts = useMemo(() => {
    return products
      .filter((p) => !!p.product && p.product!.title)
      .sort((a, b) => compareProducts(a.product!, b.product!));
  }, [products, countryPriceGroupIds]);

  const nonEmptyCountryPriceGroups = useMemo(
    (): string[] =>
      countryPriceGroupIds?.length ? countryPriceGroupIds : [NO_PRICE_GROUP],
    [countryPriceGroupIds],
  );

  const productsByGroup = useMemo(() => {
    const res: Record<string, ProductWithCountryGroup[]> = {};
    for (const group of nonEmptyCountryPriceGroups) {
      res[group] = getGroupProducts(relevantProducts, group);
    }
    return res;
  }, [nonEmptyCountryPriceGroups, relevantProducts]);

  const pricesArray = useMemo(() => {
    return nonEmptyCountryPriceGroups.map((group) => {
      const products = productsByGroup[group];
      const incentive = computeIncentive(products, duration);
      return sumProducts(products, numberOfParticipants, duration, incentive);
    });
  }, [
    nonEmptyCountryPriceGroups,
    productsByGroup,
    duration,
    numberOfParticipants,
  ]);

  const severalCountries = nonEmptyCountryPriceGroups.length > 1;
  const hasGroupWithMultipleCountries = Object.keys(countryByPriceGroups).some(
    (groupId) => countryByPriceGroups[groupId]?.length >= 2,
  );

  const globalProducts = useMemo(() => {
    return relevantProducts
      .filter(
        (op) =>
          op.product?.type &&
          op.product?.feature !== Feature.SEND_INVOICE &&
          ![
            ProductType.PARTICIPANT_FEE,
            ProductType.INCENTIVE,
            ProductType.CREDITS,
          ].includes(op.product?.type),
      )
      .map(({ product }) => product) as Product[];
  }, [relevantProducts]);

  const sendInvoiceProduct = useMemo(
    () =>
      relevantProducts.find(
        (op) => op.product?.feature === Feature.SEND_INVOICE,
      )?.product,
    [relevantProducts],
  );
  const creditProducts = useMemo(
    () =>
      toProductArr(
        relevantProducts.filter(
          (op) => op.product?.type === ProductType.CREDITS,
        ),
      ),
    [relevantProducts],
  );

  const { total, totalNoTax, taxes } = totals;
  const shouldApplyVat = taxes > 0;

  return (
    <div className={classNames(className, classes.container)}>
      {!totalOnly && (
        <>
          {nonEmptyCountryPriceGroups.map((group) => (
            <ProductListSection
              key={group}
              products={toProductArr(productsByGroup[group]).filter(
                ({ type }) =>
                  [ProductType.PARTICIPANT_FEE, ProductType.INCENTIVE].includes(
                    type,
                  ),
              )}
              duration={duration}
              title={t('billing.studyPrice.recruitment')}
              countryKeys={
                severalCountries ? countryByPriceGroups[group] : undefined
              }
              numberOfParticipants={
                !severalCountries ? numberOfParticipants : undefined
              }
              displayNumberOfParticipant={!severalCountries}
              displayCountriesInSeparateRow={hasGroupWithMultipleCountries}
              defaultOpened={defaultOpened}
              displayPerParticipantChip
            />
          ))}
          {!!globalProducts.length && (
            <ProductListSection
              products={globalProducts}
              numberOfParticipants={numberOfParticipants}
              duration={duration}
              title={t('billing.studyPrice.support')}
              defaultOpened={defaultOpened}
            />
          )}
        </>
      )}
      {!!sendInvoiceProduct && (
        <ProductListSection
          product={sendInvoiceProduct}
          numberOfParticipants={numberOfParticipants}
          duration={duration}
        />
      )}
      {creditProducts.map((p) => (
        <ProductListSection product={p} numberOfParticipants={1} />
      ))}
      <div className={classes.hr} />
      {!severalCountries && !totalOnly && (
        <SubTotal totalExclTaxes={totalNoTax} taxes={taxes} />
      )}
      {severalCountries && (
        <MinMaxTotal
          prices={pricesArray}
          shouldApplyVat={shouldApplyVat}
          numberOfParticipants={numberOfParticipants}
          column
        />
      )}

      <div className={classes.total}>
        <div className={classes.label}>
          {severalCountries
            ? t('billing.studyPrice.maxTotal')
            : t('billing.studyPrice.total')}
        </div>
        <div className={classes.price}>{toEuros(total, true)}</div>
        {!!tooltip && (
          <QuestionTooltip title={tooltip} className={classes.tooltip} />
        )}
      </div>
    </div>
  );
};
